"use client";

import type { AppRouter } from "@newform-monorepo/backend/src/router";
import type { createTRPCReact } from "@trpc/react-query";
import { createContext, type ReactNode, useContext } from "react";

/**
 * The tRPC React client type used throughout the app.
 * This is the return type of createTRPCReact<AppRouter>().
 */
export type TRPCClient = ReturnType<typeof createTRPCReact<AppRouter>>;

interface MetaPlatformContextValue {
  trpc: TRPCClient;
  orgSlug?: string;
}

const MetaPlatformContext = createContext<MetaPlatformContextValue | null>(
  null,
);

export function useMetaPlatform(): MetaPlatformContextValue {
  const ctx = useContext(MetaPlatformContext);
  if (!ctx) {
    throw new Error(
      "useMetaPlatform must be used within a <MetaPlatformProvider>",
    );
  }
  return ctx;
}

/**
 * Access the tRPC utils (for cache invalidation).
 * Equivalent to trpc.useUtils() but through the SDK context.
 */
export function useMetaPlatformUtils() {
  const { trpc } = useMetaPlatform();
  return trpc.useUtils();
}

interface MetaPlatformProviderProps {
  trpc: TRPCClient;
  orgSlug?: string;
  children: ReactNode;
}

/**
 * Provides the tRPC client and org context to all Meta SDK hooks.
 *
 * Usage:
 *   import { trpc } from "@/lib/trpc-client";
 *   import { MetaPlatformProvider } from "@newform/platform-sdk/src/meta";
 *
 *   <MetaPlatformProvider trpc={trpc} orgSlug={slug}>
 *     <App />
 *   </MetaPlatformProvider>
 */
export function MetaPlatformProvider({
  trpc,
  orgSlug,
  children,
}: MetaPlatformProviderProps) {
  return (
    <MetaPlatformContext.Provider value={{ trpc, orgSlug }}>
      {children}
    </MetaPlatformContext.Provider>
  );
}
